import { env, isClientRuntime } from '@skin-seoul/env-utils';

import type { AxiosInstance } from 'axios';
import axios from 'axios';
import qs from 'qs';
import { cartTokenQueue } from '../cart/utils/cartTokenQueue';
import { attachRecaptchaInterceptor } from '../recaptcha';
import { attachServerLoggingInterceptor } from './loggingInterceptor';
import { getServiceLogger } from './serviceLogger';

const DEFAULT_CART_URL = 'https://dev.skin-seoul.com/api';

const convertServerOriginToCartURL = (url: string) => {
  switch (url) {
    case 'https://dev-api.skin-seoul.com':
      return 'https://dev.skin-seoul.com/api';
    case 'https://staging-api.skin-seoul.com':
      return 'https://staging.skin-seoul.com/api';
    case 'https://api.skin-seoul.com':
      return 'https://skin-seoul.com/api';
    default:
      return DEFAULT_CART_URL;
  }
};

const getCartBaseURL = (): string => {
  return convertServerOriginToCartURL(env('NEXT_PUBLIC_ENV_SERVER_ORIGIN'));
};

export const createSkinSeoulCartClient = (_baseURL?: string): AxiosInstance => {
  const initialBaseURL = _baseURL ? convertServerOriginToCartURL(_baseURL) : getCartBaseURL();

  const isServer = !isClientRuntime();

  getServiceLogger().debug('[service/cartClient] Creating HTTP client:', {
    isServer,
    baseURL: initialBaseURL,
  });

  const instance = axios.create({
    baseURL: initialBaseURL,
    withCredentials: true,
    adapter: 'fetch',
    paramsSerializer: (params) => qs.stringify(params, { indices: false }),
  });

  instance.interceptors.request.use((config) => {
    // isClientRuntime, not typeof window: React Native leaves `window` undefined,
    // which used to skip this whole block and drop the cart-token header on native.
    if (isClientRuntime()) {
      const currentBaseURL = getCartBaseURL();
      const configBaseURL = config.baseURL || instance.defaults.baseURL;

      if (currentBaseURL !== DEFAULT_CART_URL && configBaseURL !== currentBaseURL) {
        getServiceLogger().warn('[service/cartClient] Race condition detected - fixing baseURL:', {
          from: configBaseURL,
          to: currentBaseURL,
        });
        config.baseURL = currentBaseURL;
        instance.defaults.baseURL = currentBaseURL;
      }

      const cartToken = cartTokenQueue.peek();
      if (cartToken) {
        config.headers['cart-token'] = cartToken;
      }
    }
    return config;
  });

  // reCAPTCHA interceptor (client-side only)
  if (!isServer) {
    attachRecaptchaInterceptor(instance);
  }

  attachServerLoggingInterceptor(instance, 'cartClient');

  return instance;
};

// TODO: Need to rename non domain specific client
// Will merge with loyaltyClient
export let cartClient: AxiosInstance = createSkinSeoulCartClient();

export const setCartClient = (instance: AxiosInstance) => {
  cartClient = instance;
};
